import * as Sentry from "@sentry/react";
import { Adb, AdbDaemonTransport } from "@yume-chan/adb";
import { AdbDaemonWebUsbDeviceManager } from "@yume-chan/adb-daemon-webusb";
import AdbWebCredentialStore from "@yume-chan/adb-credential-web";
import { PackageManager } from "@yume-chan/android-bin";
import { fetchVirtualAssistantVideoBlob } from "@utils/api-communication/RestApiCli";
import {
  VAVideoFetchError,
  VAVideoFailureReason,
  VAVideoFailedEntry,
  VAVideoDownloadResult,
} from "types/vaVideoTypes";
import { HardwareModels } from "types/productTypes";
import {
  AdbHardwareProps,
  mapAdbPropsToHardwareModel,
  needsVendorPackageProbe,
} from "./hardwareDetection";
import {
  WIFI_REQUEST_MIN_APP_VERSION,
  supportsWifiRequestFile,
  wifiRequestJson,
} from "./wifiProvisioning";
import { toAsciiSafeJson } from "./managementConfigFile";
import {
  DeviceWifiState,
  parseCurrentConnection,
  parseSavedNetworks,
  parseScanResults,
  parseWifiMacAddress,
  ScannedWifiNetwork,
} from "./wifiDeviceState";
import { API_KEY_CONFIG_FILENAME, apiKeyConfigJson } from "./apiKeyConfig";
import { canVerifyPermissions, ungrantedPermissions } from "./permissionState";
import { quoteForInnerShell, quoteShellArg } from "./shellQuote";
import {
  ApkFileCheck,
  checkApkFile,
  NO_HEX_TOOL,
  onDeviceDownloadCommand,
} from "./onDeviceDownload";
import {
  getAllowedAppOps,
  getGrantedAppPermissions,
  installsFromShellStaging,
  runsAsDeviceOwner,
} from "./headsetSetupSteps";
import {
  HTC_DEVICE_ADMIN,
  HTC_RELEASE_ACTION,
  HTC_RELEASE_RECEIVER,
  isDeviceAdminActive,
  isRefusedForAccounts,
  parseAccountTypes,
  parseDeviceOwners,
  parseReleaseResult,
} from "./htcDeviceOwner";
import { isExpectedUsbTeardownError } from "./transportTeardown";
import {
  APP_FILES_DIR,
  PACKAGE_NAME,
  getVAAssetDevicePath,
  isVAAudioAsset,
} from "./deviceStoragePaths";
import { errorMessage } from "@utils/errorMessage";
import {
  countBytesThrough,
  parseContentLength,
  DownloadProgressCb,
  PROGRESS_REPORT_INTERVAL_MS,
} from "./downloadProgress";

export { PACKAGE_NAME };
export let installedApkPath = "/sdcard/Download/oculera.apk";
export const apkBaseUrl = "https://vfa-apk-bucket.s3.eu-central-1.amazonaws.com/oculeraApk/";
const PERMISSION_SETTLE_MS = 8000;

export function getDownloadUrlForLog(url: string): string {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    return segments[segments.length - 1] || parsed.pathname || url;
  } catch {
    const noQuery = url.split("?")[0];
    const segments = noQuery.split("/").filter(Boolean);
    return segments[segments.length - 1] || noQuery;
  }
}

let gAdb: Adb | null = null;
let pm: PackageManager | null = null;
let sessionToken = 0;
let sessionLostListeners: Array<(error: unknown) => void> = [];

let lastUsbDevice: USBDevice | null = null;
// store last connected device serial for easy access
let lastSerial: string | null = null;
let lastHardwareModel: HardwareModels | null = null;
let lastHardwareProps: AdbHardwareProps | null = null;
// store last device info
let lastDevice: {
  battery: number | null;
  puiVersion: string;
  version: string;
  hardwareModel: string;
  serialNo?: string;
  pid?: string;
} | null = null;

// Helper: read a ReadableStream and decode to string
async function readStreamToString(stream: unknown): Promise<string> {
  if (!stream) return "";
  const reader = (stream as ReadableStream<Uint8Array>).getReader();
  const decoder = new TextDecoder();
  let result = "";
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    if (value) {
      if (typeof value === "string") {
        result += value;
      } else if (value instanceof Uint8Array || ArrayBuffer.isView(value)) {
        result += decoder.decode(value as Uint8Array, { stream: true });
      } else if (typeof value === "object" && value !== null && "byteLength" in (value as any)) {
        // Handles ArrayBuffer-like objects without relying on instanceof (which can fail with stricter typings)
        const ab = value as any;
        result += decoder.decode(new Uint8Array(ab), { stream: true });
      } else {
        try {
          result += String(value);
        } catch (e) {
          // ignore
        }
      }
    }
  }
  // flush
  result += decoder.decode();
  try {
    if (typeof (reader as any).releaseLock === "function") {
      await (reader as any).releaseLock();
    }
  } catch {}
  return result;
}

async function adbShell(cmd: string): Promise<string> {
  if (!gAdb) throw new Error("ADB not connected");
  const sock = await gAdb.createSocket("shell:" + cmd);
  try {
    return await readStreamToString(sock.readable as unknown);
  } finally {
    try {
      await sock.close();
    } catch {}
  }
}

async function getPropSafe(name: string): Promise<string> {
  if (!gAdb) return "";
  try {
    const value = await gAdb.getProp(name);
    return (value || "").toString().trim();
  } catch {
    return "";
  }
}

function describeHardwareProps(props: AdbHardwareProps | null): string {
  if (!props) return "no props";
  return [
    `pxr=${props.pxrProductName || "-"}`,
    `manufacturer=${props.manufacturer || "-"}`,
    `brand=${props.brand || "-"}`,
    `device=${props.device || "-"}`,
    `name=${props.name || "-"}`,
    ...(props.packages !== undefined
      ? [`vendorPackages=${props.packages.trim().split(/\r?\n/).filter(Boolean).join(",") || "-"}`]
      : []),
  ].join(", ");
}

async function listVendorPackages(): Promise<string> {
  try {
    return await adbShell("pm list packages 2>/dev/null | grep -iE 'com\\.(dpvr|deepoon)' || true");
  } catch {
    return "";
  }
}

export async function detectHardwareModel(): Promise<HardwareModels> {
  if (!gAdb) throw new Error("ADB not connected");
  if (lastHardwareModel) return lastHardwareModel;
  const [pxrProductName, manufacturer, model, brand, device, name] = await Promise.all([
    getPropSafe("sys.pxr.product.name"),
    getPropSafe("ro.product.manufacturer"),
    getPropSafe("ro.product.model"),
    getPropSafe("ro.product.brand"),
    getPropSafe("ro.product.device"),
    getPropSafe("ro.product.name"),
  ]);
  const props: AdbHardwareProps = {
    pxrProductName,
    manufacturer,
    model,
    brand,
    device,
    name,
  };
  if (needsVendorPackageProbe(props)) {
    props.packages = await listVendorPackages();
  }
  lastHardwareProps = props;
  lastHardwareModel = mapAdbPropsToHardwareModel(props);
  return lastHardwareModel;
}

export async function connectADB(log?: (msg: string) => void) {
  try {
    // If already connected, avoid reconnecting
    if (gAdb) {
      if (log) log("Already connected to a device");
      return true;
    }
    const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
    if (!manager) throw new Error("WebUSB not supported (Chromium + HTTPS required)");
    const device = await manager.requestDevice();
    if (!device) throw new Error("Device selection cancelled");
    const connection = await device.connect();
    const transport = await AdbDaemonTransport.authenticate({
      serial: device.serial ?? "usb-device",
      connection,
      credentialStore: new AdbWebCredentialStore("My Web ADB Key"),
    });
    gAdb = new Adb(transport);
    pm = new PackageManager(gAdb);
    lastUsbDevice = device.raw;
    watchTransport(gAdb, ++sessionToken, lastUsbDevice);
    // Only set serial here; device info is populated by populateDeviceInfo()
    lastSerial = device.serial ?? null;
    const hardwareModel = await detectHardwareModel();
    if (lastSerial === null && hardwareModel !== HardwareModels.HtcFocusVision) {
      lastSerial = "usb-device";
    }
    if (log) {
      log("Model: " + (lastHardwareProps?.model ?? ""));
      log(`Detected hardware: ${hardwareModel} (${describeHardwareProps(lastHardwareProps)})`);
      // Log serial number for UI
      log("Serial: " + (lastSerial ?? ""));
      const listSock = await gAdb.createSocket("shell:pm list packages | head -n 5");
      const first5 = await readStreamToString(listSock.readable as unknown);
      try {
        await listSock.close();
      } catch {}
      log((first5 || "").trim());
      log("Ready: VR start and APK install buttons enabled");
    }
    return true;
  } catch (e: any) {
    const msg = (e?.message || String(e || "")) as string;
    if (log) log("Error: " + msg);
    // Provide a helpful hint for common device-in-use errors
    if (/already in use|already in used|in used by another program/i.test(msg)) {
      if (log)
        log(
          "Hint: The device appears claimed by another program (Android Studio, adb, scrcpy). Try closing those apps or run 'adb kill-server', then reconnect.",
        );
    }
    throw e;
  }
}

// New: populate device info separately (call after successful connection)
export async function populateDeviceInfo(log?: (msg: string) => void) {
  if (!gAdb) throw new Error("ADB not connected");
  try {
    // battery
    let batteryLevel: number | null = null;
    try {
      const battOut = await adbShell("dumpsys battery 2>/dev/null || true");
      const m = battOut.match(/level\s*:?\s*(\d+)/i);
      if (m) batteryLevel = Number(m[1]);
    } catch (e) {
      batteryLevel = null;
    }

    // puiVersion
    let puiVer = "";
    try {
      puiVer = await getPuiVersion();
    } catch (e) {
      puiVer = "";
    }

    // system version
    let sysVer = "";
    try {
      const v = await gAdb.getProp("ro.build.version.release");
      sysVer = (v || "").toString().trim();
    } catch (e) {
      sysVer = "";
    }

    // apk version for PACKAGE_NAME
    let apkVersion = "";
    try {
      const dumpsysPkg = await adbShell(`dumpsys package ${PACKAGE_NAME} 2>/dev/null || true`);
      let m = dumpsysPkg.match(/versionName[:=]\s*([^\s\n\r]+)/i);
      if (!m) {
        m = dumpsysPkg.match(/versionName\s*[:=]?\s*"([^"\n\r]+)"/i);
      }
      if (m) apkVersion = (m[1] || "").trim();
    } catch (e) {
      apkVersion = "";
    }

    // hardware model
    let hw = await getPropSafe("sys.pxr.product.name");
    if (!hw) {
      hw = await getPropSafe("ro.product.model");
    }

    const chosenVersion = apkVersion || sysVer || "";
    const model = await detectHardwareModel();
    const serialNo = lastSerial ?? undefined;
    const pid = serialNo ? model + "-" + serialNo : undefined;
    lastDevice = {
      battery: batteryLevel,
      puiVersion: puiVer,
      version: chosenVersion,
      hardwareModel: hw,
      serialNo,
      pid,
    };

    if (log && lastDevice) {
      log(`Battery: ${lastDevice.battery ?? "n/a"}`);
      log(`PUI: ${lastDevice.puiVersion}`);
      log(`Version: ${lastDevice.version}`);
      log(`Hardware: ${lastDevice.hardwareModel}`);
    }

    return lastDevice;
  } catch (e) {
    lastDevice = null;
    throw e;
  }
}

export function getSerial(): string | null {
  return lastSerial;
}

export function getDeviceInfo(): {
  battery: number | null;
  puiVersion: string;
  version: string;
  hardwareModel: string;
  serialNo?: string;
  pid?: string;
} | null {
  return lastDevice;
}

export async function getPuiVersion(): Promise<string> {
  if (!gAdb) throw new Error("ADB not connected");
  const val = await gAdb.getProp("ro.system.build.id");
  return (val || "").toString().trim();
}

export async function getApkVersion(packageName = PACKAGE_NAME): Promise<string> {
  if (!gAdb) throw new Error("ADB not connected");
  const dumpsysPkg = await adbShell(`dumpsys package ${packageName} 2>/dev/null || true`);
  let m = dumpsysPkg.match(/versionName[:=]\s*([^\s\n\r]+)/i);
  if (!m) m = dumpsysPkg.match(/versionName\s*[:=]?\s*"([^"\n\r]+)"/i);
  return m ? (m[1] || "").trim() : "";
}

const SHELL_STAGED_APK_PATH = "/data/local/tmp/oculera.apk";

async function stageApkForInstall(apkPath: string, log?: (msg: string) => void) {
  log?.(`Copying ${apkPath} to ${SHELL_STAGED_APK_PATH} ...`);
  const out = await adbShell(
    `cp ${quoteShellArg(apkPath)} ${SHELL_STAGED_APK_PATH} && echo __STAGED__`,
  );
  if (!out.includes("__STAGED__")) {
    throw new Error(`Could not copy the APK to ${SHELL_STAGED_APK_PATH}: ${out.trim()}`);
  }
  return SHELL_STAGED_APK_PATH;
}

export async function installApk(
  apkPath = "/sdcard/Download/oculera.apk",
  log?: (msg: string) => void,
) {
  if (!gAdb || !pm) throw new Error("Connect to device first");
  const staged = installsFromShellStaging(await detectHardwareModel());
  try {
    const installPath = staged ? await stageApkForInstall(apkPath, log) : apkPath;
    if (log) log(`Installing from device: ${installPath}`);
    try {
      await pm.install([installPath], { grantRuntimePermissions: true, requestDowngrade: true });
    } catch (pmErr: any) {
      if (log) log("pm install reported: " + (pmErr?.message || pmErr));
      if (staged) throw pmErr;
    }
    if (log) log("✓ Install complete");
  } catch (e: any) {
    if (log) log("Install error: " + (e?.message || e));
    throw e;
  } finally {
    if (staged) await adbShell(`rm -f ${SHELL_STAGED_APK_PATH}`).catch(() => "");
  }
}

const normalizeVersion = (v: string) => (v || "").trim().replace(/^v/i, "");

export async function verifyInstalledVersion(
  expectedVersion: string,
  log?: (msg: string) => void,
  packageName = PACKAGE_NAME,
) {
  const installed = await getApkVersion(packageName);
  log?.(`Installed version: ${installed || "(none)"}, expected: ${expectedVersion}`);
  if (!installed) {
    throw new Error(`${packageName} is not installed after the update`);
  }
  if (normalizeVersion(installed) !== normalizeVersion(expectedVersion)) {
    throw new Error(
      `Installed version ${installed} does not match the downloaded version ${expectedVersion}`,
    );
  }
  log?.("✓ Installed version verified");
}

export async function uninstallApp(packageName = PACKAGE_NAME, log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");

  if (log) log(`Uninstalling: ${packageName}…`);
  try {
    if (pm && typeof pm.uninstall === "function") {
      await pm.uninstall(packageName);
      if (log) log("✓ Uninstalled (PackageManager)");
      return;
    }
    const res = await adbShell(`pm uninstall ${packageName}`);
    if (log) log(String(res).trim());
    if (!/Success/i.test(String(res))) throw new Error(String(res) || "Uninstall failed");
    if (log) log("✓ Uninstalled");
  } catch (e: any) {
    if (log) log("Uninstall error: " + (e?.message || e));
    throw e;
  }
}

const MANAGEMENT_CONFIG_PATH = "/sdcard/Oculera/config/managementConfig.json";

async function isRemoteDirectory(remotePath: string): Promise<boolean> {
  const out = await adbShell(
    `[ -d ${quoteShellArg(remotePath)} ] && echo __IS_DIR__ || echo __NO_DIR__`,
  );
  return out.includes("__IS_DIR__");
}

async function removeRemoteDirectoryAt(remotePath: string, log?: (msg: string) => void) {
  if (!(await isRemoteDirectory(remotePath))) return;
  log?.(`Directory found at ${remotePath}, removing it before push ...`);
  await adbShell(`rm -rf ${quoteShellArg(remotePath)}`);
  if (await isRemoteDirectory(remotePath)) {
    throw new Error(`Could not remove the directory at ${remotePath}`);
  }
  log?.(`✓ Removed directory: ${remotePath}`);
}

async function pushFile(remotePath: string, content: string, log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  await removeRemoteDirectoryAt(remotePath, log);
  const bytes = new TextEncoder().encode(content);
  const blob = new Blob([bytes]);
  const sync = await gAdb.sync();
  try {
    log?.(`[sync] push → ${remotePath} (${blob.size} bytes)`);
    await sync.write({
      filename: remotePath,
      file: blob.stream() as unknown as any,
      permission: 0o644,
    });
  } finally {
    (sync as any)?.dispose?.();
  }
  log?.(`✓ Written: ${remotePath}`);
}

export async function pushConfig(log?: (msg: string) => void) {
  const content = "open_guide:0\n------\nhome_pkg:" + PACKAGE_NAME + "\n------";
  await pushFile("/sdcard/config.txt", content, log);
}

export async function pushConfig1(log?: (msg: string) => void) {
  const content =
    "AutoDelete 0\nForceTOC 0\nForceNOUI 1\nForceReset6Dof 1\nheight 1.65\nForceClose 0\nForceStart 0\n#circle_r 10000\n#circle_pcount 36";
  await pushFile("/sdcard/SeethroughSetting/Config/Config1.txt", content, log);
}

async function readDeviceOwners(): Promise<string[]> {
  return parseDeviceOwners(await adbShell("dpm list-owners"));
}

export async function provisionDeviceOwner(log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  const owners = await readDeviceOwners();
  if (owners.includes(HTC_DEVICE_ADMIN)) {
    log?.("✓ The app is already the device owner");
    return;
  }
  if (owners.length > 0) {
    throw new Error(
      `Another app is the device owner (${owners.join(", ")}). Remove it or factory reset the headset, then try again.`,
    );
  }
  const cmd = `dpm set-device-owner ${HTC_DEVICE_ADMIN}`;
  log?.(`$ ${cmd}`);
  const out = (await adbShell(cmd)).trim();
  if (out) log?.(out);
  if (!(await readDeviceOwners()).includes(HTC_DEVICE_ADMIN)) {
    if (isRefusedForAccounts(out)) {
      const types = parseAccountTypes(await adbShell("dumpsys account"));
      const listed = types.length > 0 ? ` (${types.join(", ")})` : "";
      throw new Error(
        `Android refused to make the app the device owner because of the account(s) on the headset${listed}. Remove them in the headset settings, then try again.`,
      );
    }
    throw new Error(`The app could not become the device owner: ${out || "no output"}`);
  }
  log?.("✓ The app is the device owner");
}

const DEVICE_ADMIN_REMOVAL_TIMEOUT_MS = 10000;
const DEVICE_ADMIN_REMOVAL_POLL_MS = 500;

async function waitForDeviceAdminRemoval(log?: (msg: string) => void) {
  const deadline = Date.now() + DEVICE_ADMIN_REMOVAL_TIMEOUT_MS;
  while (isDeviceAdminActive(await adbShell("dumpsys device_policy"))) {
    if (Date.now() >= deadline) {
      throw new Error(
        "The app is still a device admin, so it cannot be uninstalled yet. Try again in a moment.",
      );
    }
    await new Promise((resolve) => setTimeout(resolve, DEVICE_ADMIN_REMOVAL_POLL_MS));
  }
  log?.("✓ The app is no longer a device admin");
}

export async function releaseDeviceOwner(log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  const cmd = `am broadcast -a ${HTC_RELEASE_ACTION} -n ${HTC_RELEASE_RECEIVER}`;
  log?.(`$ ${cmd}`);
  const out = (await adbShell(cmd)).trim();
  if (out) log?.(out);
  const result = parseReleaseResult(out);
  switch (result.status) {
    case "released":
      log?.("✓ The app is no longer the device owner");
      await waitForDeviceAdminRemoval(log);
      return;
    case "notDeviceOwner":
      log?.("The app is not the device owner");
      await waitForDeviceAdminRemoval(log);
      return;
    case "failed":
      throw new Error(`The app could not give up the device owner role: ${result.reason}`);
    case "noAnswer":
      log?.(
        "⚠ The app did not answer the release request (not installed, or a build without it). If the uninstall fails, only a factory reset removes the app.",
      );
  }
}

export async function grantPermissions(log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  const hardwareModel = await detectHardwareModel();
  if (runsAsDeviceOwner(hardwareModel)) await provisionDeviceOwner(log);
  const sdkInt = parseInt(await getPropSafe("ro.build.version.sdk"), 10);
  const permissions = getGrantedAppPermissions(hardwareModel);
  const cmds = [
    ...permissions.map((permission) => `pm grant ${PACKAGE_NAME} ${permission}`),
    ...getAllowedAppOps(hardwareModel, sdkInt).map(
      (op) => `appops set ${PACKAGE_NAME} ${op} allow`,
    ),
  ];
  for (const cmd of cmds) {
    log?.(`$ ${cmd}`);
    const out = (await adbShell(cmd)).trim();
    if (out) log?.(out);
  }
  if (hardwareModel !== HardwareModels.Dpvr) {
    log?.("Launching app so the grants stick ...");
    try {
      await startApp(log);
      await new Promise((resolve) => setTimeout(resolve, PERMISSION_SETTLE_MS));
    } catch (error) {
      log?.(`⚠ App launch failed, the grants may not survive a reboot: ${errorMessage(error)}`);
    }
  }
  const dump = await adbShell(`dumpsys package ${PACKAGE_NAME} 2>/dev/null || true`);
  if (!canVerifyPermissions(dump)) {
    log?.("⚠ Could not read the granted permissions back from the device");
    return;
  }
  const missing = ungrantedPermissions(dump, permissions);
  if (missing.length > 0) {
    throw new Error(`Permissions still not granted: ${missing.join(", ")}`);
  }
  log?.("✓ Permissions granted");
}

async function startApp(log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  const cmd = `monkey -p ${PACKAGE_NAME} -c android.intent.category.LAUNCHER 1`;
  log?.(`$ ${cmd}`);
  const out = (await adbShell(cmd)).trim();
  if (out) log?.(out);
  if (/no activities found|aborted/i.test(out)) {
    throw new Error(`Failed to launch ${PACKAGE_NAME}: ${out}`);
  }
  log?.("✓ App launched");
}

const DPVR_DEFAULT_LAUNCHER = "com.deepoon.DpnLauncher";
const DPVR_CONTROLLER_COMPONENT = "com.deepoon.dpnapkcontroller/.ApkCommandReceiver";
const DPVR_CONTROL_ACTION = "com.deepoon.dpnController.PACKAGE_CONTROL";

async function setDpvrLauncher(launcherPkg: string, log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  const cmds = [
    `settings put global autostart_third_app "${launcherPkg}"`,
    `am broadcast -n ${DPVR_CONTROLLER_COMPONENT} -a "${DPVR_CONTROL_ACTION}" -f 32 -e operation "setnewlauncher" -e launcherPkg "${launcherPkg}"`,
  ];
  for (const cmd of cmds) {
    log?.(`$ ${cmd}`);
    const out = (await adbShell(cmd)).trim();
    if (out) log?.(out);
  }
}

export async function enableDpvrKioskMode(log?: (msg: string) => void) {
  await setDpvrLauncher(PACKAGE_NAME, log);
  log?.("✓ Kiosk mode enabled");
}

export async function disableDpvrKioskMode(log?: (msg: string) => void) {
  await setDpvrLauncher(DPVR_DEFAULT_LAUNCHER, log);
  log?.("✓ Kiosk mode disabled");
}

export async function readManagementConfig(
  log?: (msg: string) => void,
): Promise<Record<string, any>> {
  if (!gAdb) throw new Error("Connect to device first");
  const raw = await adbShell(`cat ${MANAGEMENT_CONFIG_PATH}`);
  const trimmed = raw.trim();
  if (!trimmed || trimmed.includes("No such file")) {
    log?.("No management config found on device, using empty config");
    return {};
  }
  const config = JSON.parse(trimmed);
  log?.(`✓ Read management config (${Object.keys(config).length} fields)`);
  return config;
}

export async function writeManagementConfig(
  config: Record<string, any>,
  log?: (msg: string) => void,
) {
  await adbShell("mkdir -p /sdcard/Oculera/config");
  await pushFile(MANAGEMENT_CONFIG_PATH, toAsciiSafeJson(config), log);
}

export async function downloadApkViaSyncWrite(
  url: string,
  remotePath = "/sdcard/Download/oculera.apk",
  log?: (msg: string) => void,
  onProgress?: DownloadProgressCb,
) {
  if (!gAdb) throw new Error("Connect to device first");
  const safeUrl = getDownloadUrlForLog(url);
  log?.(`Downloading ${safeUrl} via browser fetch…`);

  let res: Response;

  try {
    res = await fetch(url, { redirect: "follow" });
    console.log("ok?", res.ok, "status", res.status, "final url", getDownloadUrlForLog(res.url));

    if (!res.ok) {
      throw new Error(`HTTP ${res.status} downloading ${getDownloadUrlForLog(res.url)}`);
    }
    if (!res.body) {
      throw new Error("This browser doesn't support streaming fetch bodies");
    }
  } catch (e: any) {
    // preserve actual error message
    const msg = e?.message ?? String(e);
    throw new Error(`Error fetching ${safeUrl}: ${msg}`);
  }

  log?.(`Uploading to device…`);

  const body = onProgress
    ? countBytesThrough(
        res.body as ReadableStream<Uint8Array>,
        parseContentLength(res.headers.get("content-length")),
        onProgress,
      )
    : res.body;

  const sync = await gAdb.sync();
  try {
    await sync.write({
      filename: remotePath,
      file: body as unknown as any,
      // type: LinuxFileType.File, // OPTIONAL - see note below
      permission: 0o644,
      mtime: Math.floor(Date.now() / 1000),
    });
  } finally {
    await sync.dispose();
  }

  log?.(`✅ Uploaded to ${remotePath}`);
  return { remotePath };
}

export async function deleteRemoteFile(remotePath: string, log?: (msg: string) => void) {
  if (!gAdb) throw new Error("ADB not connected");

  const q = quoteShellArg;

  const shellExists = async (p: string) => {
    const out = await adbShell(`ls -l ${q(p)} 2>/dev/null && echo __EXISTS__ || echo __MISSING__`);
    return out.includes("__EXISTS__");
  };

  // Try to expand to the "real" underlying path.
  const resolveCandidates = async (p: string): Promise<string[]> => {
    const candidates = new Set<string>();
    candidates.add(p);

    // Some Androids expose /sdcard -> /storage/emulated/0
    try {
      const real = (await adbShell(`realpath ${q(p)} 2>/dev/null || echo`)).trim();
      if (real) candidates.add(real);
    } catch {
      // ignore
    }

    // Common equivalent paths
    if (p.startsWith("/sdcard/")) candidates.add(p.replace(/^\/sdcard\//, "/storage/emulated/0/"));
    if (p.startsWith("/storage/emulated/0/"))
      candidates.add(p.replace(/^\/storage\/emulated\/0\//, "/sdcard/"));

    return Array.from(candidates);
  };

  const candidates = await resolveCandidates(remotePath);

  // 1) Prefer Sync remove (matches the same backend as sync.write)
  try {
    const sync = await gAdb.sync();
    try {
      for (const p of candidates) {
        try {
          // yume-chan adb sync supports remove() in recent versions
          await (sync as any).remove?.(p);
          log?.(`[sync] remove attempted: ${p}`);
        } catch {
          // ignore and verify below
        }
      }
    } finally {
      try {
        await (sync as any).dispose?.();
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore and fallback to shell rm below
  }

  // 2) Verify deletion across candidates
  let anyStillExists = false;
  for (const p of candidates) {
    try {
      const exists = await shellExists(p);
      if (exists) {
        anyStillExists = true;
        log?.(`Still exists after sync remove: ${p}`);
      } else {
        log?.(`Missing after sync remove: ${p}`);
      }
    } catch {
      // ignore
    }
  }
  if (!anyStillExists) {
    log?.(`Deleted existing file (if any): ${remotePath}`);
    return;
  }

  // 3) Fallback: shell rm against all candidates, then verify again
  for (const p of candidates) {
    try {
      const out = await adbShell(`rm -f ${q(p)} 2>&1; echo __RM_DONE__`);
      log?.(`rm output for ${p}: ${(out || "").trim()}`);
    } catch {
      // ignore
    }
  }

  anyStillExists = false;
  for (const p of candidates) {
    try {
      const exists = await shellExists(p);
      if (exists) {
        anyStillExists = true;
        const detail = await adbShell(`ls -l ${q(p)} 2>&1 || true`);
        log?.(`File still present: ${(detail || "").trim()}`);
      }
    } catch {
      // ignore
    }
  }

  if (!anyStillExists) {
    log?.(`Deleted existing file (if any): ${remotePath}`);
  }
}

async function remoteFileSize(remotePath: string): Promise<number> {
  const out = (
    await adbShell(`sh -c "wc -c < ${quoteForInnerShell(remotePath)} 2>/dev/null || echo 0"`)
  ).trim();
  const size = Number(out);
  return Number.isFinite(size) ? size : 0;
}

export async function downloadApk(
  url: string,
  remotePath = "/sdcard/Download/oculera.apk",
  log?: (msg: string) => void,
  onProgress?: DownloadProgressCb,
) {
  if (!gAdb) throw new Error("Connect to device first");

  const banner = (title: string, body?: string) => {
    const bar = "═".repeat(60);
    const lines = ["", bar, title];
    if (body) lines.push(body);
    lines.push(bar, "");
    log?.(lines.join("\n"));
  };

  // Ensure we don't accidentally reuse an old APK.
  await deleteRemoteFile(remotePath, log);

  if (log) log(`Attempting to download ${getDownloadUrlForLog(url)} → ${remotePath}`);

  const cmd = onDeviceDownloadCommand(remotePath, url);

  const sizePoll = onProgress
    ? window.setInterval(() => {
        void remoteFileSize(remotePath)
          .then((loaded) => {
            if (loaded > 0) onProgress(loaded);
          })
          .catch(() => {});
      }, PROGRESS_REPORT_INTERVAL_MS)
    : undefined;

  let out: string;
  try {
    out = (await adbShell(cmd)).trim();
  } finally {
    if (sizePoll !== undefined) window.clearInterval(sizePoll);
  }
  // GNU wget prints the full presigned URL.
  if (log && out) log(out.split(url).join(getDownloadUrlForLog(url)));

  // The shell always prints the status marker as the last non-empty line,
  // so parse that explicitly instead of substring-matching the whole blob
  // (a downloader error page could otherwise contain "__OK__" / "__FAIL__").
  const outputLines = out
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const statusLine = outputLines[outputLines.length - 1] ?? "";
  const downloaderOk = statusLine === "__OK__";

  // A captive portal or filtering proxy can answer with a web page while curl still exits 0,
  // so the headset's file is checked before it is trusted.
  let check = downloaderOk ? await readApkFileCheck(remotePath) : null;
  if (!downloaderOk) {
    if (out.includes("NO_DOWNLOADER")) {
      if (log) log("No downloader (curl/wget) found on device");
    } else {
      if (log)
        log(
          `On-device download did not complete successfully (status: ${statusLine || "<empty>"})`,
        );
    }
  } else if (check?.ok === false) {
    if (log) log(`The headset's download is not a valid APK: ${check.detail}`);
  }

  if (!check?.ok) {
    if (log) log("Falling back to browser download and adb sync upload");
    // Remove any partial/error file the failed curl/wget may have left behind.
    await deleteRemoteFile(remotePath, log);
    // Browser path always overwrites and sets mtime explicitly.
    await downloadApkViaSyncWrite(url, remotePath, log, onProgress);
    check = await readApkFileCheck(remotePath);
  }

  if (check.ok === false) {
    banner("✗ DOWNLOAD FAILED", check.detail);
    throw new Error(check.error);
  }
  if (!check.magicChecked && log)
    log("Warning: no hex tool (od/xxd/hexdump) on device — skipping APK magic-byte validation");

  // Only expose the path after all validation passes, so a failed download
  // doesn't leave `installedApkPath` pointing at an invalid/missing file.
  installedApkPath = remotePath;

  banner("✓ DOWNLOAD SUCCEEDED", `${remotePath} (${check.size} bytes)`);
  return { remotePath, size: check.size };
}

// Verify the file: it must exist, have a plausible size, and start with the
// ZIP magic bytes (PK\x03\x04) that every APK begins with.
// Try od → xxd → hexdump so we don't reject valid APKs on devices missing
// any single hex tool; if none are present, skip the check with a warning.
async function readApkFileCheck(remotePath: string): Promise<ApkFileCheck> {
  const path = quoteForInnerShell(remotePath);
  const sizeOutput = (await adbShell(`sh -c "wc -c < ${path} 2>/dev/null || echo __ERR__"`)).trim();
  const magicCmd =
    `sh -c "f=${path}; ` +
    `if command -v od >/dev/null 2>&1; then dd if=\\"\\$f\\" bs=1 count=4 2>/dev/null | od -An -v -tx1 | tr -d ' \\n'; ` +
    `elif command -v xxd >/dev/null 2>&1; then xxd -l 4 -p \\"\\$f\\" 2>/dev/null | tr -d ' \\n'; ` +
    `elif command -v hexdump >/dev/null 2>&1; then dd if=\\"\\$f\\" bs=1 count=4 2>/dev/null | hexdump -v -e '1/1 \\"%02x\\"'; ` +
    `else echo ${NO_HEX_TOOL}; fi"`;
  const magicOutput = (await adbShell(magicCmd)).trim();
  return checkApkFile(remotePath, sizeOutput, magicOutput);
}

export function onAdbSessionLost(listener: (error: unknown) => void): () => void {
  sessionLostListeners.push(listener);
  return () => {
    sessionLostListeners = sessionLostListeners.filter((entry) => entry !== listener);
  };
}

function clearAdbSession() {
  sessionToken += 1;
  gAdb = null;
  pm = null;
  lastUsbDevice = null;
  lastSerial = null;
  lastDevice = null;
  lastHardwareModel = null;
  lastHardwareProps = null;
}

const SESSION_CLOSE_TIMEOUT_MS = 5000;

async function closeWithTimeout(adb: Adb): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const closing = adb.close();
  closing.catch(() => {});
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error("Closing the USB session timed out")),
      SESSION_CLOSE_TIMEOUT_MS,
    );
  });
  try {
    await Promise.race([closing, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function releaseUsbDevice(device: USBDevice | null) {
  if (!device) return;
  try {
    await device.close();
  } catch {}
}

const closedTransports = new WeakSet<Adb>();

async function closeTransportOnce(adb: Adb, device: USBDevice | null): Promise<void> {
  if (closedTransports.has(adb)) return;
  closedTransports.add(adb);
  try {
    await closeWithTimeout(adb);
  } catch (error) {
    await releaseUsbDevice(device);
    throw error;
  }
}

function releaseTransport(adb: Adb, device: USBDevice | null) {
  void closeTransportOnce(adb, device).catch(() => {});
}

function notifySessionLost(error: unknown) {
  sessionLostListeners.forEach((listener) => {
    try {
      listener(error);
    } catch {}
  });
}

function watchTransport(adb: Adb, token: number, device: USBDevice | null) {
  adb.disconnected.then(
    () => {
      if (token !== sessionToken) {
        releaseTransport(adb, device);
        return;
      }
      clearAdbSession();
      notifySessionLost(undefined);
    },
    (error: unknown) => {
      if (token !== sessionToken) {
        releaseTransport(adb, device);
        return;
      }
      clearAdbSession();
      releaseTransport(adb, device);
      if (!isExpectedUsbTeardownError(error)) {
        Sentry.captureException(error, {
          tags: { area: "webusb-adb" },
          extra: { phase: "transport-disconnected" },
        });
      }
      notifySessionLost(error);
    },
  );
}

export async function disconnectADB() {
  const adb = gAdb;
  const device = lastUsbDevice;
  clearAdbSession();
  if (!adb) return;
  notifySessionLost(undefined);
  try {
    await closeTransportOnce(adb, device);
  } catch (error) {
    if (!isExpectedUsbTeardownError(error)) {
      Sentry.captureException(error, {
        tags: { area: "webusb-adb" },
        extra: { phase: "disconnect" },
      });
    }
  }
}

export function isAdbConnected(): boolean {
  return !!gAdb;
}

/**
 * Fetches a VA video as a Blob via the dedicated backend blob-proxy endpoint
 * (/updates/virtual-assistant/video-blob). The backend streams the S3 bytes directly
 * so the browser never touches S3 and there are no CORS issues.
 */
async function fetchVAVideoBlob(videoPath: string, signal?: AbortSignal): Promise<Blob> {
  return fetchVirtualAssistantVideoBlob(videoPath, { signal });
}

export async function downloadAndPushVAAssetsForLanguage(
  languageCode: string,
  languageLabel: string,
  assetNames: string[],
  onProgress: (completedFiles: number, totalFiles: number) => void,
  signal?: AbortSignal,
  log?: (msg: string) => void,
  shouldLogAssetName?: (videoName: string) => boolean,
): Promise<VAVideoDownloadResult> {
  if (!gAdb) throw new Error("Connect to device first");

  const failedFiles: VAVideoFailedEntry[] = [];
  const model = await detectHardwareModel();

  for (let i = 0; i < assetNames.length; i++) {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");

    const videoName = assetNames[i];
    const devicePath = getVAAssetDevicePath(model, languageCode, videoName);
    const videoPath = isVAAudioAsset(videoName)
      ? `${languageCode}/${videoName}`
      : `${languageCode}/${videoName}.mp4`;
    const nameSuffix = (shouldLogAssetName?.(videoName) ?? true) ? ` ${videoName}` : "";

    log?.(`[${languageLabel}] [${i + 1}/${assetNames.length}]${nameSuffix}`);

    try {
      const blob = await fetchVAVideoBlob(videoPath, signal);

      if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");

      const deviceDir = devicePath.substring(0, devicePath.lastIndexOf("/"));
      await adbShell(`mkdir -p "${deviceDir}"`);

      const sync = await gAdb.sync();
      try {
        await sync.write({
          filename: devicePath,
          file: blob.stream() as unknown as any,
          permission: 0o644,
          mtime: Math.floor(Date.now() / 1000),
        });
      } finally {
        await sync.dispose();
      }

      log?.(`✅ [${languageLabel}]${nameSuffix}`);
    } catch (e: any) {
      if (e?.name === "AbortError") throw e;

      const reason: VAVideoFailureReason =
        e instanceof VAVideoFetchError ? e.reason : "network_error";
      const msg = e?.message ?? String(e);

      failedFiles.push({ videoName, language: languageLabel, reason, error: msg });
    }

    onProgress(i + 1, assetNames.length);
  }

  return { failedFiles };
}

const ignoringFailure = (cmd: string) => `${cmd} 2>/dev/null || true`;

/**
 * `cmd wifi list-scan-results` is refused for lack of root on Android 10, hence the
 * `dumpsys wifiscanner` fallback. Android rescans by itself every few seconds, so no
 * trigger is needed — and none may be attempted: changing radio state surfaces the Pico
 * system Wi-Fi panel, which drops the headset out of the Oculera app.
 */
async function readNetworksInRange(): Promise<ScannedWifiNetwork[]> {
  const scanned = parseScanResults(await adbShell(ignoringFailure("cmd wifi list-scan-results")));
  if (scanned.length > 0) return scanned;
  return parseScanResults(await adbShell(ignoringFailure("dumpsys wifiscanner")));
}

export async function readDeviceWifiState(log?: (msg: string) => void): Promise<DeviceWifiState> {
  if (!gAdb) throw new Error("Connect to device first");

  const [scannedNetworks, wifiDump] = await Promise.all([
    readNetworksInRange(),
    adbShell(ignoringFailure("dumpsys wifi")),
  ]);
  const connection = parseCurrentConnection(wifiDump);

  log?.(`${scannedNetworks.length} network(s) in range`);
  log?.(connection ? `Device is on "${connection.ssid}"` : "Device is not on any network");
  return {
    scannedNetworks,
    savedNetworks: parseSavedNetworks(wifiDump),
    connection,
    macAddress: parseWifiMacAddress(wifiDump),
  };
}

// App-private storage, which adb can still write to but other apps cannot read.
const WIFI_REQUEST_DIR = APP_FILES_DIR;
const WIFI_REQUEST_PATH = `${WIFI_REQUEST_DIR}/wifi.json`;
const WIFI_REQUEST_PARTIAL_PATH = `${WIFI_REQUEST_PATH}.part`;
const API_KEY_CONFIG_PATH = `${WIFI_REQUEST_DIR}/${API_KEY_CONFIG_FILENAME}`;
const API_KEY_CONFIG_PARTIAL_PATH = `${API_KEY_CONFIG_PATH}.part`;

/**
 * Writes the request the headset consumes on its trigger gesture. Pushed to a `.part` name and
 * renamed, so the headset cannot read a half-written file. The passphrase is never logged.
 */
export async function provisionWifi(ssid: string, password: string, log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  const appVersion = await getApkVersion();
  if (!supportsWifiRequestFile(appVersion)) {
    throw new Error(
      `Wi-Fi setup over USB needs headset ${WIFI_REQUEST_MIN_APP_VERSION} or newer. ` +
        `This device reports ${appVersion || "no version"}.`,
    );
  }

  await adbShell(`mkdir -p ${WIFI_REQUEST_DIR}`);
  // Pushed under a temporary name and renamed, so a gesture during the push cannot read a
  // half-written file. Logged as the final path: the temporary one is an implementation detail.
  await pushFile(WIFI_REQUEST_PARTIAL_PATH, wifiRequestJson({ ssid, password }));
  const renameOutput = (
    await adbShell(`mv ${WIFI_REQUEST_PARTIAL_PATH} ${WIFI_REQUEST_PATH}`)
  ).trim();
  if (renameOutput) {
    await adbShell(ignoringFailure(`rm ${WIFI_REQUEST_PARTIAL_PATH}`));
    throw new Error(`Could not place the Wi-Fi request on the device: ${renameOutput}`);
  }
  log?.(`✓ Wi-Fi request for "${ssid}" written to ${WIFI_REQUEST_PATH}`);
}

export async function provisionApiKeyConfig(configKey: string, log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");

  await adbShell(`mkdir -p ${WIFI_REQUEST_DIR}`);
  await pushFile(API_KEY_CONFIG_PARTIAL_PATH, apiKeyConfigJson(configKey), log);
  const renameOutput = (
    await adbShell(`mv ${API_KEY_CONFIG_PARTIAL_PATH} ${API_KEY_CONFIG_PATH}`)
  ).trim();
  if (renameOutput) {
    await adbShell(ignoringFailure(`rm ${API_KEY_CONFIG_PARTIAL_PATH}`));
    throw new Error(`Could not place the API key config on the device: ${renameOutput}`);
  }
  log?.(`✓ API key config written to ${API_KEY_CONFIG_PATH}`);
}

export async function rebootDevice(log?: (msg: string) => void) {
  if (!gAdb) throw new Error("Connect to device first");
  if (log) log("Rebooting device...");
  try {
    await adbShell("reboot");
    if (log) log("Reboot command sent");
  } catch (e: any) {
    if (log) log("Reboot error: " + (e?.message || e));
    throw e;
  } finally {
    await disconnectADB();
    if (log) log("USB session closed; reconnect the headset once it is back up.");
  }
}
